import type { ScaleGroup, ChartDimensions } from '../../types';
import { CHART_CONSTANTS } from '../../constants';
import { formatCompactCurrency, formatNumber } from '../../utils';

interface GridLinesProps {
  dimensions: ChartDimensions;
  maxValue: number;
  hasDualAxis: boolean;
  leftAxisGroup?: ScaleGroup;
  rightAxisGroup?: ScaleGroup;
}

/**
 * Renders horizontal grid lines and Y-axis labels.
 * Supports dual-axis mode with left/right labels.
 */
export function GridLines({
  dimensions,
  maxValue,
  hasDualAxis,
  leftAxisGroup,
  rightAxisGroup,
}: GridLinesProps) {
  const { paddingLeft, paddingRight, chartWidth, getY } = dimensions;
  const { GRID_PERCENTAGES, COLORS } = CHART_CONSTANTS;

  const formatAxisLabel = (value: number, group: ScaleGroup | undefined) => {
    if (!group) return formatCompactCurrency(value);
    const hasCurrency = group.metrics.some(m => m.isCurrency);
    if (hasCurrency) return formatCompactCurrency(value);
    return formatNumber(Math.round(value));
  };

  const leftMax = hasDualAxis && leftAxisGroup ? leftAxisGroup.maxValue : maxValue;
  const rightMax = hasDualAxis && rightAxisGroup ? rightAxisGroup.maxValue : null;

  return (
    <>
      {GRID_PERCENTAGES.map((pct, i) => (
        <g key={i}>
          <line
            x1={paddingLeft}
            y1={getY(leftMax * pct, leftMax)}
            x2={chartWidth - paddingRight}
            y2={getY(leftMax * pct, leftMax)}
            stroke={COLORS.GRID}
            strokeDasharray={pct === 0 ? '' : '4'}
          />
          {/* Left axis labels */}
          <text
            x={paddingLeft - 8}
            y={getY(leftMax * pct, leftMax) + 4}
            textAnchor="end"
            fontSize="11"
            fill={hasDualAxis ? COLORS.LEFT_AXIS : COLORS.LABEL}
            fontWeight={hasDualAxis ? 500 : 400}
          >
            {formatAxisLabel(leftMax * pct, leftAxisGroup)}
          </text>
          {/* Right axis labels (if dual axis) */}
          {hasDualAxis && rightMax && (
            <text
              x={chartWidth - paddingRight + 8}
              y={getY(rightMax * pct, rightMax) + 4}
              textAnchor="start"
              fontSize="11"
              fill={COLORS.RIGHT_AXIS}
              fontWeight={500}
            >
              {formatAxisLabel(rightMax * pct, rightAxisGroup)}
            </text>
          )}
        </g>
      ))}
    </>
  );
}
